/**
 * PlanChangeDialog Component
 * Shows detailed summary of plan changes before confirmation
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  useTheme,
  Alert,
  Chip
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  ArrowForwardOutlined as ArrowIcon,
  CheckOutlined as CheckIcon,
  TrendingDownOutlined as DowngradeIcon,
  TrendingUpOutlined as UpgradeIcon,
  ViewInAr as CubeIcon
} from '@mui/icons-material';
import { PlanTier } from '@/types/planTypes';
import { planTiers, planFeatures } from '@/data/plansData';

interface PlanChangeDialogProps {
  open: boolean;
  onClose: () => void;
  currentPlanId: PlanTier;
  targetPlanId: PlanTier;
  onConfirm: () => void;
}

export const PlanChangeDialog: React.FC<PlanChangeDialogProps> = ({
  open,
  onClose,
  currentPlanId,
  targetPlanId,
  onConfirm
}) => {
  const theme = useTheme();
  const [isConfirming, setIsConfirming] = useState(false);

  const currentPlan = planTiers.find(p => p.id === currentPlanId);
  const targetPlan = planTiers.find(p => p.id === targetPlanId);

  const isDowngrade = useMemo(() => {
    const tierOrder: PlanTier[] = ['starter', 'professional', 'enterprise'];
    const currentIndex = tierOrder.indexOf(currentPlanId);
    const targetIndex = tierOrder.indexOf(targetPlanId);
    return targetIndex < currentIndex;
  }, [currentPlanId, targetPlanId]);

  const isUpgrade = !isDowngrade;

  // Calculate feature changes
  const { lostFeatures, gainedFeatures, changedFeatures } = useMemo(() => {
    const lost: string[] = [];
    const gained: string[] = [];
    const changed: Array<{ name: string; from: any; to: any }> = [];

    planFeatures.forEach(feature => {
      const currentValue = feature.values[currentPlanId];
      const targetValue = feature.values[targetPlanId];

      // Check if feature is being lost (had it, now don't)
      if (currentValue === true && targetValue === false) {
        lost.push(feature.name);
      }
      // Check if feature is being gained
      else if (currentValue === false && targetValue === true) {
        gained.push(feature.name);
      }
      // Check if numeric/string value is changing
      else if (
        currentValue !== targetValue &&
        typeof currentValue !== 'boolean' &&
        typeof targetValue !== 'boolean'
      ) {
        changed.push({
          name: feature.name,
          from: currentValue,
          to: targetValue
        });
      }
    });

    return { lostFeatures: lost, gainedFeatures: gained, changedFeatures: changed };
  }, [currentPlanId, targetPlanId]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    await onConfirm();
    setIsConfirming(false);
  };

  if (!currentPlan || !targetPlan) return null;

  const currentPrice = typeof currentPlan.price === 'number' ? currentPlan.price : 0;
  const targetPrice = typeof targetPlan.price === 'number' ? targetPlan.price : 0;
  const priceDiff = targetPrice - currentPrice;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Change summary
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ borderRadius: '6px' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Plan Change Visual */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default
          }}
        >
          {/* Current Plan */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '8px',
                backgroundColor: theme.palette.grey[200],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1
              }}
            >
              <CubeIcon />
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {currentPlan.name}
            </Typography>
            <Chip
              label="Annual"
              size="small"
              sx={{ mt: 0.5, height: 20, fontSize: '11px' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ${currentPrice.toFixed(2)} user/month
            </Typography>
          </Box>

          {/* Arrow */}
          <ArrowIcon sx={{ color: 'text.secondary' }} />

          {/* Target Plan */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '8px',
                backgroundColor: theme.palette.primary.main + '20',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1,
                color: theme.palette.primary.main
              }}
            >
              <CubeIcon />
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {targetPlan.name}
            </Typography>
            <Chip
              label="Annual"
              size="small"
              color="primary"
              sx={{ mt: 0.5, height: 20, fontSize: '11px' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ${targetPrice.toFixed(2)} user/month
            </Typography>
          </Box>
        </Box>

        {/* Price Change */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              Seats
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                1 used
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Price change
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: 'line-through' }}
              >
                ${currentPrice.toFixed(2)}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ${targetPrice.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Automation Credits */}
        {changedFeatures.some(f => f.name.toLowerCase().includes('credit')) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Automation credits
            </Typography>
            {changedFeatures
              .filter(f => f.name.toLowerCase().includes('credit'))
              .map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 0.5
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {feature.name}
                  </Typography>
                  <Typography variant="body2">
                    {feature.from} → {feature.to}
                  </Typography>
                </Box>
              ))}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Review Changes */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Review your changes
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {isDowngrade
              ? 'You will lose access to these features'
              : 'You will gain access to these features'}
          </Typography>

          {/* Lost Features (for downgrades) */}
          {isDowngrade && lostFeatures.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {lostFeatures.slice(0, 8).map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <DowngradeIcon
                    sx={{ fontSize: 16, color: 'error.main', mt: 0.25 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {feature}
                  </Typography>
                </Box>
              ))}
              {lostFeatures.length > 8 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  + {lostFeatures.length - 8} more features
                </Typography>
              )}
            </Box>
          )}

          {/* Gained Features (for upgrades) */}
          {isUpgrade && gainedFeatures.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {gainedFeatures.slice(0, 8).map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1
                  }}
                >
                  <UpgradeIcon
                    sx={{ fontSize: 16, color: 'success.main', mt: 0.25 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {feature}
                  </Typography>
                </Box>
              ))}
              {gainedFeatures.length > 8 && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  + {gainedFeatures.length - 8} more features
                </Typography>
              )}
            </Box>
          )}

          {/* Changed Limits */}
          {changedFeatures
            .filter(f => !f.name.toLowerCase().includes('credit'))
            .slice(0, 5)
            .map((feature, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  mb: 1
                }}
              >
                <DowngradeIcon
                  sx={{
                    fontSize: 16,
                    color: isDowngrade ? 'warning.main' : 'success.main',
                    mt: 0.25
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {feature.name}: {feature.from} → {feature.to}
                </Typography>
              </Box>
            ))}
        </Box>

        {/* Downgrade Alert */}
        {isDowngrade && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Downgrade</strong>
            </Typography>
            <Typography variant="caption">
              Changes will take effect immediately. You may lose access to certain
              features and data limits.
            </Typography>
          </Alert>
        )}

        {/* Upgrade Alert */}
        {isUpgrade && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Upgrade</strong>
            </Typography>
            <Typography variant="caption">
              Changes will take effect immediately. You'll be charged a prorated amount
              for the remainder of your billing period.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px'
          }}
        >
          Keep current plan <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>ESC</Typography>
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isDowngrade ? 'error' : 'primary'}
          disabled={isConfirming}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '14px'
          }}
        >
          {isConfirming
            ? 'Processing...'
            : isDowngrade
            ? 'Confirm downgrade'
            : 'Confirm upgrade'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

